import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TREE_ACTIONS, TreeComponent, TreeModule, TreeNode } from '@ali-hm/angular-tree-component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { OrganizationFile } from '../../../../models/entities/organization-file';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { DocumentsService } from '../../../../services/communication/documents.service';

export const FOLDER_PICKER_ROOT_ID = -1;
export const FOLDER_PICKER_ROOT_NAME = 'Documents';

export interface FolderPickerSelection {
    id: number;
    path: string[];
}

interface FolderNode {
    id: number;
    name: string;
    fileName: string;
    directory: boolean;
    parentId?: number | null;
    children: FolderNode[];
}

/** Org document folders as a searchable tree under a synthetic "Documents" root (id -1); emits the chosen folder and its path. */
@UntilDestroy()
@Component({
    selector: 'app-folder-picker',
    templateUrl: './folder-picker.component.html',
    styleUrls: ['./folder-picker.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatProgressSpinnerModule,
        TreeModule,
    ],
})
export class FolderPickerComponent implements OnInit, OnChanges {
    @Input() organizationId?: number;
    @Input() selectedFolderId: number | null = null;
    @Input() showPathPreview = true;

    @Output() readonly folderSelected = new EventEmitter<FolderPickerSelection>();
    @Output() readonly loaded = new EventEmitter<OrganizationFile[]>();
    @Output() readonly loadFailed = new EventEmitter<void>();

    @ViewChild('tree') treeComponent?: TreeComponent;

    readonly rootId = FOLDER_PICKER_ROOT_ID;
    readonly options = { allowDrag: false, useCheckbox: false, actionMapping: { mouse: { click: TREE_ACTIONS.ACTIVATE } } };

    isLoading = true;
    hasFailed = false;
    nodes: FolderNode[] = [];
    searchQuery = '';
    selectedPath: string[] = [];

    private _selectedId: number | null = null;

    constructor(
        private readonly _documents: DocumentsService,
        private readonly _cdr: ChangeDetectorRef,
    ) {}

    get pathDisplay(): string {
        return this.selectedPath.join(' / ');
    }

    get hasSelection(): boolean {
        return this.selectedPath.length > 0;
    }

    ngOnInit(): void {
        this._selectedId = this.selectedFolderId;
        this.load();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedFolderId'] && !changes['selectedFolderId'].firstChange) {
            this._selectedId = this.selectedFolderId;
            this._applySelection();
        }
    }

    load(): void {
        this.isLoading = true;
        this.hasFailed = false;
        this._cdr.markForCheck();
        const orgId = this.organizationId ?? LegFiJwtService.read()?.orgId;
        this._documents.getFlatDocumentsForOrganization(orgId).pipe(untilDestroyed(this)).subscribe({
            next: (res: Object) => {
                const files = ((res as Object[]) ?? []).map((doc) => new OrganizationFile(doc));
                this.nodes = [{
                    id: FOLDER_PICKER_ROOT_ID,
                    directory: true,
                    fileName: FOLDER_PICKER_ROOT_NAME,
                    name: FOLDER_PICKER_ROOT_NAME,
                    children: this._createTree(files.filter((f) => f.directory)),
                }];
                this.isLoading = false;
                this._cdr.markForCheck();
                this.loaded.emit(files);
            },
            error: () => {
                this.isLoading = false;
                this.hasFailed = true;
                this._cdr.markForCheck();
                this.loadFailed.emit();
            },
        });
    }

    /** The tree model exists only once tree-root has rendered, so the root opens and the preselect lands here. */
    onTreeInitialized(): void {
        this.treeComponent?.treeModel.getNodeById(FOLDER_PICKER_ROOT_ID)?.expand();
        this._applySelection();
    }

    /** Every activation, mouse, keyboard or preselect, lands here once. */
    onNodeClick(event: { node?: TreeNode }): void {
        const node = event.node;
        if (!node || node.id === undefined) {
            return;
        }
        this._select(node);
    }

    onSearchChange(query: string): void {
        this.searchQuery = query.toLowerCase().trim();
        const tree = this.treeComponent;
        if (!tree) {
            return;
        }
        if (!this.searchQuery) {
            tree.treeModel.doForAll((node: TreeNode) => node.show());
            tree.treeModel.collapseAll();
            tree.treeModel.getNodeById(FOLDER_PICKER_ROOT_ID)?.expand();
            return;
        }
        tree.treeModel.doForAll((node: TreeNode) => {
            if (this._nodeName(node).includes(this.searchQuery)) {
                node.show();
                let parent = node.parent;
                while (parent && !parent.data?.virtual) {
                    parent.expand();
                    parent.show();
                    parent = parent.parent;
                }
                return;
            }
            let hasMatchingDescendant = false;
            node.doForAll((child: TreeNode) => {
                if (child !== node && this._nodeName(child).includes(this.searchQuery)) {
                    hasMatchingDescendant = true;
                }
            });
            if (hasMatchingDescendant) {
                node.show();
                node.expand();
            } else {
                node.hide();
            }
        });
    }

    clearSearch(): void {
        this.onSearchChange('');
    }

    private _applySelection(): void {
        const tree = this.treeComponent;
        if (!tree || this._selectedId === null) {
            return;
        }
        const node = tree.treeModel.getNodeById(this._selectedId);
        if (!node) {
            return;
        }
        let parent = node.parent;
        while (parent && !parent.data?.virtual) {
            parent.expand();
            parent = parent.parent;
        }
        tree.treeModel.setActiveNode(node, true);
    }

    private _select(node: TreeNode): void {
        this._selectedId = node.id;
        this.selectedPath = this._buildPath(node);
        this._cdr.markForCheck();
        this.folderSelected.emit({ id: node.id, path: [...this.selectedPath] });
    }

    private _buildPath(node: TreeNode): string[] {
        const path: string[] = [];
        let current: TreeNode | null = node;
        while (current) {
            path.unshift(current.data.fileName || current.data.name);
            current = current.parent?.data?.virtual ? null : current.parent;
        }
        return path;
    }

    private _nodeName(node: TreeNode): string {
        return (node.data.fileName || node.data.name || '').toLowerCase();
    }

    private _createTree(directories: OrganizationFile[]): FolderNode[] {
        const byId: Record<number, FolderNode> = Object.create(null);
        directories.forEach((item) => {
            byId[item.id] = { id: item.id, fileName: item.fileName, name: item.fileName, directory: true, parentId: item.parentId, children: [] };
        });
        directories.forEach((item) => {
            if (item.parentId && byId[item.parentId]) {
                byId[item.parentId].children = [...byId[item.parentId].children, byId[item.id]];
            }
        });
        return directories.filter((item) => !item.parentId).map((item) => byId[item.id]);
    }
}
